import { useQueryClient } from 'react-query';

import { reshapeAutomation } from 'modules/bookkeep/apis/prepare-payable/useGetPayableQuery/graphql-reshaper';
import { type PayableId } from 'modules/payable/models';
import { useQuery } from 'src/core/api/hooks/useQuery';
import { type QueryState } from 'src/core/api/queryState';
import { type PayableFromRestApi } from 'src/core/modules/bookkeep/apis/useGetPayable';

import type { Payable } from './payable';
import { type RawResult, query } from './query';
import {
  reshapeAllocation,
  reshapeAllocationPeriod,
  reshapeCounterparty,
  reshapeCustomField,
  reshapeDocumentaryEvidence,
  reshapeDocumentaryEvidenceCannotBeProvided,
  reshapeItemLine,
  reshapeMember,
  reshapeMileageDetails,
  reshapeCreditNoteDetails,
  reshapePayableState,
  reshapePayableType,
  reshapePerDiem,
  reshapeSupplier,
  reshapeTeam,
} from './reshaper';
import {
  rejectUnexpectedValue,
  TypeGuardError,
} from '../../../../../../../utils/switchGuard';

export type PayableGraphQLRawResponse = RawResult;

export type PayableGraphQlVariables = { payableId: string };

export const getPayableQueryKey = (payableId: string | null) =>
  `payablePanel::${payableId}`;

export const useUpdatePayableQueryCache = () => {
  const queryClient = useQueryClient();

  return (
    payableId: PayableId | null,
    payableUpdate: Partial<RawResult['payable']>,
  ) => {
    const payable: RawResult['payable'] | undefined =
      queryClient.getQueryData<PayableGraphQLRawResponse>(
        getPayableQueryKey(payableId),
      )?.payable;

    // Skip update if we don't have a cached payable or if the update has not a higher version
    if (
      (payable?.version ?? Number.POSITIVE_INFINITY) >
      (payableUpdate?.version ?? 0)
    ) {
      return;
    }

    if (payableId) {
      queryClient.setQueryData<PayableGraphQLRawResponse>(
        getPayableQueryKey(payableId),
        {
          payable: {
            id: payableId,
            ...payable,
            ...payableUpdate,
          } as RawResult['payable'],
        },
      );
    }
  };
};

export const useInvalidateGetPayableQueryCache = () => {
  const queryClient = useQueryClient();

  return async (payableId: PayableId | null): Promise<void> => {
    queryClient.invalidateQueries(getPayableQueryKey(payableId));
  };
};

export function reshapeRestApiToLocal(payable: PayableFromRestApi): Payable {
  const reshapeType = (
    payableType: PayableFromRestApi['type'],
    subType: PayableFromRestApi['subType'],
  ): Payable['type'] => {
    switch (payableType) {
      case 'perDiemExpense':
        return 'perDiemAllowance';
      case 'mileageExpense':
        return 'mileageAllowance';
      case 'claimedBill':
        return 'claimedBill';
      case 'reverseBill':
        return 'reversal';
      case 'bill':
        return ['physical', 'singlePurchase', 'subscription'].includes(subType)
          ? 'cardPurchase'
          : 'invoicePurchase';
      default:
        return rejectUnexpectedValue('type', payableType);
    }
  };
  const reshapeSubType = (
    subType: PayableFromRestApi['subType'],
  ): Payable['subtype'] => {
    switch (subType) {
      case 'creditNote':
        return 'creditNote';
      case 'subscription':
        return 'subscriptionCard';
      case 'singlePurchase':
        return 'singleUseCard';
      case 'multiUseCard':
        return 'multiUseCard';
      case 'physical':
        return 'plasticCard';
      default:
        return undefined;
    }
  };

  const reshapeDocumentaryEvidenceFromRestApi = (
    documentaryEvidence: PayableFromRestApi['documentaryEvidence'],
  ): Payable['documentaryEvidence'] => {
    if (!documentaryEvidence) {
      return undefined;
    }

    const reshapeCompliances = (
      compliances: Exclude<
        PayableFromRestApi['documentaryEvidence'],
        undefined
      >['compliances'],
    ): Exclude<Payable['documentaryEvidence'], undefined>['compliances'] => {
      if (!compliances.spainEreceipt) {
        return undefined;
      }
      return {
        ...(compliances.spainEreceipt && {
          spainEreceipt: {
            receipt: compliances.spainEreceipt.receipt
              ? {
                  ...compliances.spainEreceipt.receipt,
                }
              : undefined,
            closure: compliances.spainEreceipt.closure
              ? {
                  ...compliances.spainEreceipt.closure,
                }
              : undefined,
          },
        }),
      };
    };

    const reshapeAttachments = (
      attachments: Exclude<
        PayableFromRestApi['documentaryEvidence'],
        undefined
      >['attachments'],
    ): Exclude<Payable['documentaryEvidence'], undefined>['attachments'] => {
      return attachments.map((attachment) => ({
        ...attachment,
        mediaType: attachment.mimetype,
        attachedAt: attachment.attachedAt
          ? new Date(attachment.attachedAt)
          : undefined,
        compliances: attachment.compliances
          ? reshapeCompliances(attachment.compliances)
          : undefined,
      }));
    };

    switch (documentaryEvidence.type) {
      case 'declarationOfMissingInvoice':
      case 'declarationOfMissingCreditNote':
      case 'perDiemEntry':
      case 'mileageEntry':
      case 'invoice':
        return {
          ...documentaryEvidence,
          type: 'invoice',
          invoiceNumber:
            'invoiceNumber' in documentaryEvidence
              ? documentaryEvidence.invoiceNumber
              : undefined,
          compliances: reshapeCompliances(documentaryEvidence.compliances),
          validity: {
            ...documentaryEvidence.validity,
            isValid: documentaryEvidence.validity.valid,
            reason:
              'reason' in documentaryEvidence.validity
                ? documentaryEvidence.validity.reason
                : '',
          },
          attachments: reshapeAttachments(documentaryEvidence.attachments),
        };
      case 'creditNote':
        return {
          ...documentaryEvidence,
          type: 'creditNote',
          creditNoteNumber: documentaryEvidence.creditNoteNumber,
          compliances: reshapeCompliances(documentaryEvidence.compliances),
          attachments: reshapeAttachments(documentaryEvidence.attachments),
          validity: {
            isValid: documentaryEvidence.validity.valid,
            reason: documentaryEvidence.validity.valid
              ? ''
              : documentaryEvidence.validity.reason,
          },
        };
      default:
        throw new TypeGuardError(
          documentaryEvidence,
          'unknown documentaryEvidence type',
        );
    }
  };

  const reshapeItemLines = (
    itemLines: PayableFromRestApi['itemLines'],
  ): Payable['itemLines'] => {
    return itemLines.map((itemLine) => {
      return {
        ...itemLine,
        taxAmount: itemLine.vatAmount,
        taxAccount: itemLine.taxAccount
          ? {
              ...itemLine.taxAccount,
              id: itemLine.taxAccount.id,
              name: itemLine.taxAccount.name,
              isArchived: itemLine.taxAccount.isArchived,
              rate:
                itemLine.taxAccount?.type === 'vat'
                  ? Number(itemLine.taxAccount.rate)
                  : undefined,
            }
          : undefined,
        expenseAccount: itemLine.expenseAccount
          ? {
              ...itemLine.expenseAccount,
              id: itemLine.expenseAccount.id,
              name: itemLine.expenseAccount.name,
              isArchived: itemLine.expenseAccount.isArchived,
            }
          : undefined,
      };
    });
  };

  const reshapeCustomFields = (
    customFields: PayableFromRestApi['customFields'],
  ): Payable['customFields'] => {
    return customFields.map((customField) => {
      switch (customField.customField.type) {
        case 'boolean':
          if (!('value' in customField)) {
            throw new Error(
              `customField.value is not defined in ${customField}`,
            );
          }

          return {
            ...customField,
            type: 'boolean',
            id: customField.customField.id,
            name: customField.customField.name,
            value: customField.value,
          };
        case 'list':
          if (!('values' in customField)) {
            throw new Error(
              `customField.values is not defined in ${customField}`,
            );
          }
          return {
            ...customField,
            type: 'list',
            id: customField.customField.id,
            name: customField.customField.name,
            values: customField.values.map((value) => ({
              id: value.id,
              name: value.value,
            })),
          };
        default:
          throw new TypeGuardError(
            customField.customField,
            `customField type ${customField.customField} not supported`,
          );
      }
    });
  };

  const reshapeAllocations = (
    payableFromApi: PayableFromRestApi,
  ): Payable['allocations'] => {
    const settlementTypeReshaper = (
      settlementKind: Exclude<
        PayableFromRestApi['allocationsWithResolvedCounterparties'][number]['settlement'],
        undefined
      >['kind'],
    ): Exclude<
      Payable['allocations'][number]['settlement'],
      undefined
    >['kind'] => {
      switch (settlementKind) {
        case 'refund':
          return 'refund';
        case 'reimbursement':
          return 'reimbursement';
        case 'payment':
          return 'payment';
        // TODO: this is incorrect, but we keep it the same as old behaviour
        // for now, since its usage cares only about these 3 subtypes and
        // defaults any other to payment.
        default:
          return 'payment';
      }
    };

    return payableFromApi.allocationsWithResolvedCounterparties.map((a) => {
      return {
        ...a,
        settlement: a.settlement && {
          ...a.settlement,
          kind: settlementTypeReshaper(a.settlement.kind),
          transaction: {
            ...a.settlement.transaction,
            clearingDate: new Date(a.settlement.transaction.clearingDate),
          },
        },
        reversal: a.reversal && {
          ...a.reversal,
          documentaryEvidence: a.reversal.documentaryEvidence && {
            ...a.reversal.documentaryEvidence,
            creditNoteNumber:
              // TODO: this is incorrect, but old behaviour assumes
              // can always be provided.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              a.reversal.documentaryEvidence.creditNoteNumber!,
          },
        },
      };
    });
  };

  const reshapePayableFromApiState = (
    rawType: PayableFromRestApi['state'],
  ): Payable['state'] => {
    switch (rawType) {
      case 'created':
        return 'created';
      case 'prepared':
        return 'prepared';
      case 'notBookkept':
        return 'notBookkept';
      case 'unprepared':
        return 'unprepared';
      case 'discarded':
        return 'discarded';
      case 'to_accounting:pending':
        return 'toAccountingPending';
      case 'to_accounting:failed':
        return 'toAccountingFailed';
      case 'in_accounting':
        return 'inAccounting';
      case 'in_accounting:manually':
        return 'inAccountingManually';
      default:
        throw new Error(`Unknown payable type ${rawType}`);
    }
  };

  const reshapePayableAutomationFromApi = (
    payableAutomation: PayableFromRestApi['automation'],
  ): Payable['automation'] => {
    let reshapedTax:
      | Exclude<Payable['automation'], undefined>['tax']
      | undefined;

    if (payableAutomation?.tax) {
      if (payableAutomation.tax?.kind === 'default') {
        reshapedTax = {
          kind: 'default',
          isAppliedOnPayable: !!payableAutomation.tax.isAppliedOnPayable,
        };
      } else if (payableAutomation.tax.kind === 'recommendation') {
        reshapedTax = {
          kind: 'recommendation',
          confidence: payableAutomation.tax.confidence ?? undefined,
          isAppliedOnPayable: !!payableAutomation.tax.isAppliedOnPayable,
        };
      } else {
        reshapedTax = {
          kind: 'prediction',
          taxAccount: payableAutomation.tax.taxAccount ?? {
            id: '',
            name: '',
          },
          confidence: payableAutomation.tax.confidence ?? undefined,
          isAppliedOnPayable: !!payableAutomation.tax.isAppliedOnPayable,
        };
      }
    }

    return {
      ...payableAutomation,
      expenseAccount: payableAutomation?.expenseAccount ?? undefined,
      expenseAmount: payableAutomation?.expenseAmount ?? undefined,
      vatAccount: payableAutomation?.vatAccount ?? undefined,
      accountPayable: payableAutomation?.accountPayable ?? undefined,
      tax: reshapedTax,
      documentaryEvidenceDate: payableAutomation?.documentaryEvidenceDate && {
        ...payableAutomation.documentaryEvidenceDate,
        documentaryEvidenceDate: payableAutomation.documentaryEvidenceDate
          ?.documentaryEvidenceDate
          ? new Date(
              payableAutomation.documentaryEvidenceDate.documentaryEvidenceDate,
            )
          : undefined,
      },
    };
  };

  const reshapeMemberFromApi = (
    rawMember: PayableFromRestApi['member'],
  ): Payable['member'] => {
    if (rawMember.deletedAt) {
      return {
        type: 'suspendedUser',
        ...rawMember,
        avatar: rawMember.avatar ?? '',
      };
    }
    if (rawMember.pending) {
      return {
        type: 'invitee',
        ...rawMember,
        email: rawMember.email ?? '',
      };
    }

    return {
      type: 'user',
      ...rawMember,
      avatar: rawMember.avatar ?? '',
      email: rawMember.email ?? '',
    };
  };

  const reshapeCounterpartyFromApi = (
    payableCounterparty: PayableFromRestApi['counterparty'],
  ): Payable['counterparty'] => {
    if (payableCounterparty.kind === 'member') {
      return reshapeMemberFromApi(payableCounterparty);
    }

    return { ...payableCounterparty, type: 'supplier' };
  };

  const reshapedCounterparty = reshapeCounterpartyFromApi(payable.counterparty);

  return {
    ...payable,
    state: reshapePayableFromApiState(payable.state),
    type: reshapeType(payable.type, payable.subType),
    subtype: reshapeSubType(payable.subType),
    grossAmount: payable.amount,
    firstExportedAt: payable.firstExportedAt
      ? new Date(payable.firstExportedAt)
      : null,
    customFields: reshapeCustomFields(payable.customFields),
    itemLines: reshapeItemLines(payable.itemLines),
    documentaryEvidenceCannotBeProvided:
      payable.documentaryEvidenceCannotBeProvided,
    documentaryEvidence: reshapeDocumentaryEvidenceFromRestApi(
      payable.documentaryEvidence,
    ),
    supplier:
      payable.counterparty?.kind === 'supplier'
        ? payable.counterparty
        : undefined,
    counterparty: reshapedCounterparty,
    member: reshapeMemberFromApi(payable.member),
    team: payable.team,
    costCenter: payable.costCenter,
    allocations: reshapeAllocations(payable),
    creditNoteDetails: payable.creditNoteDetails && {
      ...payable.creditNoteDetails,
      // TODO: when graphql is not used anymore let the property be optional
      referenceInvoiceRequestId:
        payable.creditNoteDetails?.referenceInvoiceRequestId ?? undefined,
      // TODO: when graphql not used anymore use .kind where .validationAction
      // is used
      validationAction: payable.creditNoteDetails?.validationAction?.kind,
    },
    // TODO: some types here were expected undefined/null, but the backend says
    // otherwise. However, since the backend is also not using the original
    // types and this comes from server I don't know which types to trust
    mileageDetails: payable.mileage && {
      ...reshapeMileageDetails({
        ...payable.mileage,
        journey: payable.mileage.journey ?? [],
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        distanceFromMap: payable.mileage.distanceFromMap!,
        vehicle: {
          ...payable.mileage.vehicle,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          taxHorsepower: payable.mileage.vehicle.taxHorsepower!,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ownership: payable.mileage.vehicle.ownership!,
        },
      }),
    },
    perDiem: payable.perDiem && {
      ...payable.perDiem,
      duration: payable.perDiem.tripDays.length,
    },
    spendingAllocationPeriod: payable.spendingAllocationPeriod && {
      period: {
        from: new Date(payable.spendingAllocationPeriod.period.from),
        to: new Date(payable.spendingAllocationPeriod.period.to),
      },
      date: payable.spendingAllocationPeriod?.date
        ? new Date(payable.spendingAllocationPeriod.date)
        : undefined,
    },
    creationDate: new Date(payable.creationDate),
    accountingDate: payable.accountingDate
      ? new Date(payable.accountingDate)
      : undefined,
    automation: reshapePayableAutomationFromApi(payable.automation),
  };
}

export const usePayableQuery = (
  payableId: string,
  isEnabled: boolean = true,
): QueryState<Payable | undefined> => {
  return useQuery<Payable | undefined, RawResult>({
    key: getPayableQueryKey(payableId),
    request: {
      type: 'graphQL',
      target: 'v2',
      query,
      variables: {
        payableId,
      },
    },
    isEnabled,
    reshapeData({ payable: rawPayable }) {
      if (!rawPayable) {
        return;
      }

      return {
        id: rawPayable.id,
        version: rawPayable.version,
        kind: rawPayable.kind,
        type: reshapePayableType(rawPayable.type),
        subtype: rawPayable.subtype || undefined,
        state: reshapePayableState(rawPayable.state),
        description: rawPayable.description,
        grossAmount: rawPayable.grossAmount,
        functionalExchangeRate: rawPayable.functionalExchangeRate,
        functionalAmount: rawPayable.functionalAmount,
        firstExportedAt: rawPayable.firstExportedAt
          ? new Date(rawPayable.firstExportedAt)
          : null,
        counterparty: rawPayable.counterparty
          ? reshapeCounterparty(rawPayable.counterparty)
          : undefined,
        member: rawPayable.member
          ? reshapeMember(rawPayable.member)
          : undefined,
        supplier: rawPayable.supplier
          ? reshapeSupplier(rawPayable.supplier)
          : undefined,
        team: rawPayable.team ? reshapeTeam(rawPayable.team) : undefined,
        costCenter: rawPayable.costCenter,
        accountPayable: rawPayable.accountPayable,
        documentaryEvidence: rawPayable.documentaryEvidence
          ? reshapeDocumentaryEvidence(rawPayable.documentaryEvidence)
          : undefined,
        documentaryEvidenceCannotBeProvided:
          rawPayable.documentaryEvidenceCannotBeProvided
            ? reshapeDocumentaryEvidenceCannotBeProvided(
                rawPayable.documentaryEvidenceCannotBeProvided,
              )
            : undefined,
        itemLines: rawPayable.itemLines.map(reshapeItemLine),
        customFields: rawPayable.customFields.map(reshapeCustomField),
        allocations: rawPayable.allocations.map(reshapeAllocation),
        creationDate: new Date(rawPayable.creationDate),
        mileageDetails: rawPayable.mileageDetails
          ? reshapeMileageDetails(rawPayable.mileageDetails)
          : undefined,
        perDiem: rawPayable.perDiem
          ? reshapePerDiem(rawPayable.perDiem)
          : undefined,
        creditNoteDetails: rawPayable.creditNoteDetails
          ? reshapeCreditNoteDetails(rawPayable.creditNoteDetails)
          : undefined,
        spendingAllocationPeriod: rawPayable.spendingAllocationPeriod
          ? reshapeAllocationPeriod(rawPayable.spendingAllocationPeriod)
          : undefined,
        accountingDate: rawPayable.accountingDate
          ? new Date(rawPayable.accountingDate)
          : undefined,
        amortisation: rawPayable.amortisation
          ? {
              schemeId: rawPayable.amortisation.schemeId,
              schemeName: rawPayable.amortisation.schemeName,
              date: {
                from: rawPayable.amortisation.date.from,
                to: rawPayable.amortisation.date.to,
              },
            }
          : undefined,
        automation: reshapeAutomation(rawPayable.automation),
        expenseBeneficiaries: rawPayable.expenseBeneficiaries,
      };
    },
  });
};
